import React, { useState, useEffect } from 'react';
import { useSummer2026 } from '../context/Summer2026Context';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Check, Clock, DoorOpen, X, Save, Waves, Search, LogOut as LogOutIcon, Bus
} from 'lucide-react';
import { motion } from 'framer-motion';
import { CamperAvatar } from './CamperAvatar';

const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
const safeFetch = (...args) => (window.__origFetch || window.fetch)(...args);

const SESSION_COLORS = {
  full: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Full Season' },
  half1: { bg: 'bg-green-100', text: 'text-green-800', label: 'Half 1' },
  half2: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Half 2' },
  '6week': { bg: 'bg-orange-100', text: 'text-orange-800', label: '6 Weeks' },
  flex20: { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Flex 20' },
  flex25: { bg: 'bg-teal-100', text: 'text-teal-800', label: 'Flex 25' },
  flex30: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Flex 30' },
};

function formatDate(isoDate) {
  const d = new Date(isoDate + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

export default function Summer2026GroupDetail({ groupCode, groupName, campers, date, onBack, isAdmin }) {
  const { getAttendance, setAttendanceForCamper, getSwimLesson, getBusStatus } = useSummer2026();
  const [timeDialog, setTimeDialog] = useState(null);
  const [tempTime, setTempTime] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [frontDeskLogs, setFrontDeskLogs] = useState({});

  // Load lock time from backend on mount (syncs across all devices)
  useEffect(() => {
    const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? `${window.location.origin}/api` : `${process.env.REACT_APP_BACKEND_URL}/api`;
    const sf = (...args) => (window.__origFetch || window.fetch)(...args);
    sf(`${API}/summer2026/settings?key=attendance_lock_time`)
      .then(r => r.json())
      .then(d => { if (d.value) localStorage.setItem('attendance_lock_time', d.value); })
      .catch(() => {});
  }, []);

  // Attendance lock: counselors (non-admin) locked after configurable time EST
  // Admin can set the lock time in Season Management
  // June 20, 2026 = live test day, no lock
  const [lockHour, lockMinute] = (() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('attendance_lock_time') : null;
    if (saved && saved.includes(':')) {
      const [h, m] = saved.split(':').map(Number);
      return [h, m];
    }
    return [10, 0]; // default 10:00 AM
  })();

  const isLocked = !isAdmin && (() => {
    // June 20 bypass — live test day, open attendance all day
    if (date === '2026-06-20') return false;
    const now = new Date();
    const edt = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const currentMinutes = edt.getHours() * 60 + edt.getMinutes();
    const lockAtMinutes = lockHour * 60 + lockMinute;
    return currentMinutes >= lockAtMinutes;
  })();

  // Fetch front desk logs for this date
  useEffect(() => {
    (async () => {
      try {
        const res = await safeFetch(`${API}/summer2026/frontdesk/logs?date=${date}`);
        const data = await res.json();
        if (data.success) {
          const byPerson = {};
          for (const log of data.logs) {
            if (!byPerson[log.personId]) byPerson[log.personId] = [];
            byPerson[log.personId].push(log);
          }
          setFrontDeskLogs(byPerson);
        }
      } catch {}
    })();
  }, [date]);

  const q = searchQuery.trim().toLowerCase();
  const isCIT = groupCode && groupCode.toLowerCase().includes('cit');
  const sortedCampers = isCIT
    ? [...campers].sort((a, b) => {
        const last = (a.lastName || '').localeCompare(b.lastName || '');
        return last !== 0 ? last : (a.firstName || '').localeCompare(b.firstName || '');
      })
    : campers;
  const filteredCampers = q
    ? sortedCampers.filter(c => `${c.firstName} ${c.lastName}`.toLowerCase().includes(q))
    : sortedCampers;

  const summary = campers.reduce((acc, c) => {
    const att = getAttendance(c.personId, date);
    acc[att.status] = (acc[att.status] || 0) + 1;
    if (att.lateTime) acc.lateCount++;
    if (att.earlyTime) acc.earlyCount++;
    if (getSwimLesson(c.personId, date)) acc.swimCount++;
    const bus = getBusStatus(c.personId, date);
    if (bus) {
      if (bus.status === 'present') acc.busOnCount++;
      else if (bus.status === 'absent') acc.busAbsentCount++;
      else if (bus.status === 'late') acc.busLateCount++;
    }
    return acc;
  }, { present: 0, late: 0, leaving: 0, absent: 0, not_selected: 0, lateCount: 0, earlyCount: 0, swimCount: 0, busOnCount: 0, busAbsentCount: 0, busLateCount: 0 });

  const handleToggleLate = (personId) => {
    const att = getAttendance(personId, date);
    if (att.lateTime) {
      setAttendanceForCamper(personId, date, { lateTime: null, status: att.earlyTime ? 'leaving' : 'not_selected' });
    } else {
      // Auto-capture current time (Eastern)
      const timeStr = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' });
      setAttendanceForCamper(personId, date, { lateTime: timeStr, status: 'late' });
    }
  };

  const handleToggleLeaving = (personId) => {
    const att = getAttendance(personId, date);
    if (att.earlyTime) {
      setAttendanceForCamper(personId, date, { earlyTime: null, status: att.lateTime ? 'late' : 'not_selected' });
    } else {
      setTimeDialog({ personId, type: 'leaving' });
      setTempTime('');
    }
  };

  const handleSaveTime = () => {
    if (!timeDialog || !tempTime) return;
    const att = getAttendance(timeDialog.personId, date);
    setAttendanceForCamper(timeDialog.personId, date, { earlyTime: tempTime, status: att.lateTime ? 'late' : 'leaving' });
    setTimeDialog(null);
    setTempTime('');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full" data-testid="summer-group-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#0F172A] font-['Fredoka']">{groupName}</h1>
            <p className="text-sm text-[#475569]">{formatDate(date)}</p>
          </div>
          <a
            href="https://campersnapshot.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-[#0055A4] hover:bg-[#003F7D] text-white px-3 py-1.5 rounded-full text-xs font-bold transition-colors shadow-sm"
            data-testid="snapshot-link-group"
          >
            Back to Snapshot →
          </a>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4">
        {/* Swim Lesson banner — shows campers at swim so counselors know they're not late */}
        {(() => {
          const swimmers = campers.filter(c => getSwimLesson(c.personId, date));
          if (swimmers.length === 0) return null;
          return (
            <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 mb-3" data-testid="swim-lesson-banner">
              <div className="flex items-center gap-2 mb-2">
                <Waves className="w-4 h-4 text-cyan-700" />
                <p className="text-sm font-bold text-cyan-900">At Swim Lesson ({swimmers.length})</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {swimmers.map(c => {
                  const swim = getSwimLesson(c.personId, date);
                  return (
                    <span key={c.personId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 border border-cyan-200">
                      {c.firstName} {c.lastName}
                      {swim?.time && <span className="text-cyan-600 font-normal">· {swim.time}</span>}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Live front desk activity for this group */}
        {Object.keys(frontDeskLogs).length > 0 && (
          <div className="space-y-2 mb-4" data-testid="group-frontdesk-alerts">
            {Object.values(frontDeskLogs).flat().map((log, i) => (
              <div
                key={i}
                className={`rounded-xl p-3 border flex items-center gap-3 ${
                  log.logType === 'late_arrival'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-teal-50 border-teal-200'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  log.logType === 'late_arrival' ? 'bg-amber-200' : 'bg-teal-200'
                }`}>
                  {log.logType === 'late_arrival'
                    ? <Clock className="w-4 h-4 text-amber-700" />
                    : <LogOutIcon className="w-4 h-4 text-teal-700" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold ${log.logType === 'late_arrival' ? 'text-amber-900' : 'text-teal-900'}`}>
                    {log.camperName}
                  </p>
                  <p className={`text-xs ${log.logType === 'late_arrival' ? 'text-amber-700' : 'text-teal-700'}`}>
                    {log.logType === 'late_arrival' ? `Arrived late at ${log.time}` : `Early dismissal at ${log.time}`}
                    {log.signerName ? ` — signed by ${log.signerName}` : ''}
                  </p>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${
                  log.logType === 'late_arrival'
                    ? 'bg-amber-200 text-amber-800'
                    : 'bg-teal-200 text-teal-800'
                }`}>
                  {log.logType === 'late_arrival' ? 'LATE' : 'DISMISSED'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Lock banner for counselors after 10 AM */}
        {isLocked && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-center" data-testid="attendance-locked-banner">
            <p className="text-sm font-semibold text-red-800">Attendance is locked after 10:00 AM</p>
            <p className="text-xs text-red-600 mt-0.5">Contact the front desk for any changes</p>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            data-testid="group-camper-search"
            placeholder="Search camper by name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 h-10 rounded-xl border-[#E2E8F0] bg-white text-sm"
          />
          {q && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {q && (
          <p className="text-xs text-[#475569] mb-2 px-1">
            {filteredCampers.length} of {campers.length} camper{campers.length !== 1 ? 's' : ''}
          </p>
        )}

        {/* Camper list */}
        <div className="space-y-3">
          {filteredCampers.map((camper, idx) => (
            <CamperCard
              key={camper.personId}
              camper={camper}
              date={date}
              index={idx}
              onToggleLate={() => handleToggleLate(camper.personId)}
              onToggleLeaving={() => handleToggleLeaving(camper.personId)}
              fdLogs={frontDeskLogs[camper.personId] || []}
              isLocked={isLocked}
              lastNameFirst={isCIT}
            />
          ))}
        </div>

        {/* Summary */}
        <motion.div
          className="mt-6 bg-white rounded-2xl shadow-lg border border-[#E2E8F0] p-5"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-sm font-bold text-[#475569] uppercase tracking-wider mb-3">Summary</h3>
          <div className="flex flex-wrap gap-3">
            {summary.not_selected > 0 && (
              <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300 px-3 py-1.5" data-testid="summary-not-selected">
                <span className="w-4 h-4 mr-1 font-bold">?</span>{summary.not_selected} Not Selected
              </Badge>
            )}
            <Badge className="status-present px-3 py-1.5"><Check className="w-4 h-4 mr-1" />{summary.present} Present</Badge>
            <Badge className="status-late px-3 py-1.5"><Clock className="w-4 h-4 mr-1" />{summary.lateCount} Late</Badge>
            <Badge className="status-leaving px-3 py-1.5"><DoorOpen className="w-4 h-4 mr-1" />{summary.earlyCount} Leaving Early</Badge>
            <Badge className="status-absent px-3 py-1.5"><X className="w-4 h-4 mr-1" />{summary.absent} Absent</Badge>
            {summary.swimCount > 0 && (
              <Badge className="bg-cyan-100 text-cyan-800 border border-cyan-200 px-3 py-1.5"><Waves className="w-4 h-4 mr-1" />{summary.swimCount} Swim Lesson{summary.swimCount !== 1 ? 's' : ''}</Badge>
            )}
            {summary.busOnCount > 0 && (
              <Badge className="bg-green-100 text-green-800 border border-green-200 px-3 py-1.5"><Bus className="w-4 h-4 mr-1" />{summary.busOnCount} On Bus</Badge>
            )}
            {summary.busAbsentCount > 0 && (
              <Badge className="bg-red-100 text-red-800 border border-red-200 px-3 py-1.5"><Bus className="w-4 h-4 mr-1" />{summary.busAbsentCount} Absent from Bus</Badge>
            )}
            {summary.busLateCount > 0 && (
              <Badge className="bg-amber-100 text-amber-800 border border-amber-200 px-3 py-1.5"><Bus className="w-4 h-4 mr-1" />{summary.busLateCount} Late (Bus)</Badge>
            )}
            <Badge className="bg-[#0055A4] text-white px-3 py-1.5">Total: {campers.length}</Badge>
          </div>
        </motion.div>
      </div>

      {/* Early Dismissal Time Dialog */}
      <Dialog open={!!timeDialog} onOpenChange={(open) => !open && setTimeDialog(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Fredoka']">Early Dismissal</DialogTitle>
            <DialogDescription>
              Enter the time the pickup person will arrive (from call/email)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={tempTime}
              onChange={(e) => setTempTime(e.target.value)}
              placeholder="2:30 PM"
              className="h-12 rounded-xl"
              data-testid="summer-time-input"
              autoFocus
            />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setTimeDialog(null)} className="flex-1 rounded-xl">Cancel</Button>
            <Button
              onClick={handleSaveTime}
              disabled={!tempTime}
              className="flex-1 rounded-xl bg-[#0055A4] hover:bg-[#004080]"
              data-testid="summer-save-time"
            >
              <Save className="w-4 h-4 mr-2" />Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-[#E2E8F0] py-2 px-4 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-center">
        </div>
      </div>
    </div>
  );
}

function CamperCard({ camper, date, index, onToggleLate, onToggleLeaving, fdLogs, isLocked, lastNameFirst }) {
  const { getAttendance, setAttendanceForCamper, getSwimLesson, getBusStatus } = useSummer2026();
  const att = getAttendance(camper.personId, date);
  const swim = getSwimLesson(camper.personId, date);
  const busStatus = getBusStatus(camper.personId);
  const sessionInfo = SESSION_COLORS[camper.sessionType] || SESSION_COLORS.full;

  const lateLog = fdLogs.find(l => l.logType === 'late_arrival');
  const earlyLog = fdLogs.find(l => l.logType === 'early_dismissal');

  const handleStatusChange = (status) => {
    if (isLocked) return;
    if (status === 'present') {
      setAttendanceForCamper(camper.personId, date, { status: 'present', lateTime: null, earlyTime: null });
    } else if (status === 'absent') {
      setAttendanceForCamper(camper.personId, date, { status: 'absent', lateTime: null, earlyTime: null });
    }
  };

  const isNotSelected = att.status === 'not_selected';

  return (
    <motion.div
      className={`rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border p-4 ${
        isNotSelected
          ? 'bg-yellow-50 border-yellow-300'
          : 'bg-white border-[#E2E8F0]'
      }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      data-testid={`summer-camper-${camper.personId}`}
    >
      <div className="flex items-start gap-3">
        <CamperAvatar firstName={camper.firstName} lastName={camper.lastName} personId={camper.personId} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-[#0F172A]">{lastNameFirst ? `${camper.lastName}, ${camper.firstName}` : `${camper.firstName} ${camper.lastName}`}</p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${sessionInfo.bg} ${sessionInfo.text}`}>
              {sessionInfo.label}
            </span>
            {swim && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-cyan-100 text-cyan-800 border border-cyan-200"
                data-testid={`swim-lesson-${camper.personId}`}
              >
                <Waves className="w-3 h-3" />
                Swim Lesson {swim.time}
              </span>
            )}
            {busStatus && busStatus.status === 'present' && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 border border-green-200"
                data-testid={`bus-on-${camper.personId}`}
              >
                <Bus className="w-3 h-3" />
                On Bus
              </span>
            )}
            {busStatus && busStatus.status === 'absent' && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800 border border-red-200"
                data-testid={`bus-absent-${camper.personId}`}
              >
                <Bus className="w-3 h-3" />
                Absent from Bus
              </span>
            )}
            {busStatus && busStatus.status === 'late' && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                data-testid={`bus-late-${camper.personId}`}
              >
                <Bus className="w-3 h-3" />
                Late (Bus)
              </span>
            )}
            {busStatus && busStatus.status === 'unmarked' && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200"
                data-testid={`bus-unmarked-${camper.personId}`}
              >
                <Bus className="w-3 h-3" />
                Bus Unmarked
              </span>
            )}
          </div>

          {/* Late / Leaving Early pills */}
          <div className="flex flex-wrap gap-2 mt-2">
            {lateLog && (
              <Badge className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 border border-amber-200">
                <Clock className="w-3 h-3 mr-1" />Late Arrival {lateLog.time}
              </Badge>
            )}
            {earlyLog && (
              <Badge className="bg-teal-100 text-teal-800 text-xs px-2 py-0.5 border border-teal-200">
                <LogOutIcon className="w-3 h-3 mr-1" />Early Dismissal {earlyLog.time}
              </Badge>
            )}
            {att.lateTime && !lateLog && (
              <Badge className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5">
                <Clock className="w-3 h-3 mr-1" />Arrived {att.lateTime}
              </Badge>
            )}
            {att.earlyTime && !earlyLog && (
              <Badge className="bg-teal-100 text-teal-800 text-xs px-2 py-0.5">
                <DoorOpen className="w-3 h-3 mr-1" />Leaving {att.earlyTime}
              </Badge>
            )}
          </div>

          {/* Actions */}
          <div className={`flex flex-wrap gap-2 mt-3 ${isLocked ? 'opacity-50 pointer-events-none' : ''}`}>
            <Select
              value={att.status === 'not_selected' ? undefined : att.status}
              onValueChange={handleStatusChange}
              disabled={isLocked}
            >
              <SelectTrigger
                className={`w-[140px] h-9 rounded-lg text-sm ${
                  isNotSelected ? 'border-yellow-400 bg-yellow-50 text-yellow-700 font-semibold' : ''
                }`}
                data-testid={`summer-status-${camper.personId}`}
              >
                <SelectValue placeholder="-- Select --" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="present"><div className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" />Present</div></SelectItem>
                <SelectItem value="absent"><div className="flex items-center"><X className="w-4 h-4 mr-2 text-red-600" />Absent</div></SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={att.lateTime ? 'default' : 'outline'}
              size="sm"
              onClick={onToggleLate}
              className={`rounded-lg h-9 ${att.lateTime ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
              data-testid={`summer-late-${camper.personId}`}
            >
              <Clock className="w-4 h-4 mr-1" />Late
            </Button>
            <Button
              variant={att.earlyTime ? 'default' : 'outline'}
              size="sm"
              onClick={onToggleLeaving}
              className={`rounded-lg h-9 ${att.earlyTime ? 'bg-teal-500 hover:bg-teal-600' : ''}`}
              data-testid={`summer-leaving-${camper.personId}`}
            >
              <DoorOpen className="w-4 h-4 mr-1" />Leaving
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
